import API from "../../utils/api";
import { notification } from "antd";
import { AuthStore } from "../../stores/auth/auth.store";
import { AuthService } from "../../services/auth/auth.service";
import { SystemUser } from "../../models/user/user";
import { AxiosError, AxiosResponse } from "axios";
import { ErrorResponseData } from "../../models/core/response";
import history from "../../utils/history";
import { UserService } from "../../services/user/user.service";
import * as Sentry from "@sentry/react";
import {parseJwt} from "../../utils/jwt";
import {serverList} from "../../data/server-list";
import {toNumber} from "lodash";
import {AuthModel} from "../../models/auth/auth";

export class LoginController {
    static TOKEN = "tk0";
    static ACCOUNT = "def0";
    static BRANCH = "def1";
    static BRANCHES = "br0";

    // static ORIG_TOKEN = "o_tk0";
    // static ORIG_ACCOUNT = "o_def0";
    // static ORIG_BRANCH = "o_def1";
    // static CHANGED = "uchg";

    constructor(
        private readonly _authStore: AuthStore,
        private readonly _authService: AuthService,
        private readonly _userService: UserService
    ) {
        this.logout = this.logout.bind(this);
        // this.tryLoadFakeUser();
        this.init();
    }

    async login(values: {username: string, password: string}): Promise<void> {
        this._authStore.setLogging(true);
        try {
            const result = await this._authService.auth({ ...values, password: values.password ? values.password.trim() : "" });
            return this.handleResult(result);

        } catch (e: any) {
            this._authStore.setLogging(false);
            this._authStore.setInited(true);
            notification.error({ message: "Error", description: e.response.data.message });
        }
    }

    public async switchUser(values: { user?: { value: string } }): Promise<void> {
        const user = values.user?.value || null;
        if (user) {
            const result = await this._userService.switchUser(user);
            await this.handleResult(result);
        } else {
            const result = await this._userService.resetUser();
            await this.handleResult(result);
        }

        window.location.reload();
    }

    private async handleResult(result: AuthModel) {

        localStorage.setItem(LoginController.TOKEN,    result.token);
        localStorage.setItem(LoginController.BRANCH,   result.default.branch_id);
        localStorage.setItem(LoginController.ACCOUNT,  result.default.account_id);
        if (result.branches) {
            localStorage.setItem(LoginController.BRANCHES, result.branches.join(","));
        }

        this._authStore.setToken(result.token);
        this._authStore.defaultServer = serverList.get(result.default.branch_id as any);
        this._authStore.setAccess(toNumber(result.default.account_id));
        this._authStore.setUserBranch(result.default.branch_id);
        if (result.branches) {
            this._authStore.availableServers = serverList.getListByKeys(result.branches);
        }

        await this.loadUser();
        localStorage.setItem("registered", "1");
        this._authStore.setLogging(false);

        history.push("/catalog");
    }

    public async loadUser(): Promise<void> {
        try {
            const { user, attributes, branch, selectedAccess, originalUser, closed, selectedCurrencyCode, currencyCodes, lz, timezone } = await this._userService.getCurrentUserWithAttributes();
            this._authStore.setUser(user);
            this._authStore.setOriginalUsername(originalUser);
            Sentry.setUser({ email: this._authStore.user?.email, id: this._authStore.user?.id + "" });
            this._authStore.setAttributes(attributes);
            this._authStore.setUserBranch(branch);
            this._authStore.setAccess(selectedAccess);
            this._authStore.setClosed(closed);
            this._authStore.setSelectedCurrencyCode(localStorage.getItem('cc') || selectedCurrencyCode);
            this._authStore.setCurrencyCodes(currencyCodes);
            this._authStore.setLZ(lz)
            this._authStore.setTimezone(timezone)
            this._authStore.setInited(true);
        } catch (e) {
            this._authStore.setInited(true);
        }
    }

    private tryLoadUser(): void {
        this.loadUser();
    }

    async logout() {

        localStorage.removeItem(LoginController.TOKEN);
        localStorage.removeItem(LoginController.BRANCHES);
        localStorage.removeItem(LoginController.BRANCH);
        localStorage.removeItem(LoginController.ACCOUNT);

        this._authStore.token = '';
        this._authStore.setAccess(null);
        this._authStore.availableServers = [];

        this._authStore.setIsInvalidCredentials(null);
        this._authStore.setUser(null);
        this._authStore.setUserBranch("");
        this._authStore.setAccess(null);
        Sentry.configureScope((scope) => scope.setUser(null));
        history.push("/login");
    }

    async switchRole(access: number, branch: string) {
        localStorage.setItem(LoginController.BRANCH, branch);
        localStorage.setItem(LoginController.ACCOUNT, String(access));
        window.location.reload();
    }

    async setCurrencyCode(currencyCode: string) {
        this._authStore.selectedCurrencyCode = currencyCode;
        localStorage.setItem('cc', currencyCode);
        window.location.reload();
    }

    // private removeAuthCookie(): void {
    //     document.cookie = AuthStore.KEY_BEARER + "=";
    // }

    private initUnauthorizedInterceptor() {
        API.interceptors.response.use(
            (value: AxiosResponse): AxiosResponse => {
                return value;
            },
            (error: AxiosError<ErrorResponseData>) => {
                if (error.config.skipUnauthorizedHandler) {
                    return Promise.reject(error);
                }

                if (
                    error.response?.status === 401
                    && window.location.pathname !== "/login"
                    && window.location.pathname !== "/forgot-password"
                    && window.location.pathname !== "/forgot-password/complete"
                    && window.location.pathname !== "/sign-up"
                ) {
                   this.logout();
                }
                return Promise.reject(error);
            }
        );
    }

    private init(): void {

        this._authStore.setToken(localStorage.getItem(LoginController.TOKEN) || "");
        this._authStore.setAccess(parseInt(localStorage.getItem(LoginController.ACCOUNT) || "") || 0);
        this._authStore.setUserBranch(localStorage.getItem(LoginController.BRANCH) || "");

        if (this._authStore.branch) {
            this._authStore.defaultServer = serverList.get(this._authStore.branch as any);
            this._authStore.availableServers = serverList.getListByKeys((localStorage.getItem(LoginController.BRANCHES) || "").split(","));
        } else {
            this._authStore.defaultServer  = serverList.getDefault();
        }

        this.initUnauthorizedInterceptor();
        this.tryLoadUser();
    }

    public generateUri = (baseUri: string, params: Record<string, string> | string = '', useDefaultServer?: boolean) => {
        let obj;
        if (typeof params === 'string') {
            obj = Object.fromEntries(new URLSearchParams(params));
        } else if (params && typeof params === 'object' && !Array.isArray(params)) {
            obj = { ...params };
        } else {
            throw new TypeError('params must be an object or query string');
        }

        obj.token = String(this._authStore.token);
        obj.api_access = String(this._authStore.getAccess());

        const server = useDefaultServer ? serverList.getDefault() : this._authStore.defaultServer;

        return 'https://' + server + baseUri + '?' + (new URLSearchParams(obj).toString());
    }}
